import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import envConfig from '../../utils/EnvironmentConfigs.js';
import CopyableEmail from '../../components/CopyableEmail';
import DescriptionWithTags from '../../components/DescriptionWithTags/DescriptionWithTags';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import { firstResolvedDisplayName } from '../../utils/DisplayValueUtils.js';
import { getFirstAttributeValue, getUserAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { normalizeUserTypeCode } from '../../utils/UserTypeUtils.js';

const extractSupervisorIdFromUser = (user) => {
    return String(
        user?.supervisor ??
        user?.supervisorUuid ??
        user?.supervisorId ??
        getUserAttributeValue(user, ['supervisor', 'Supervisor']) ??
        ''
    ).trim();
};

const extractUserTypeFromUser = (user) => {
    return normalizeUserTypeCode(
        user?.UserType ??
        user?.userType ??
        user?.user_type ??
        getUserAttributeValue(user, ['UserType', 'userType']) ??
        ''
    );
};

const extractUserTypeFromToken = (tokenDetails) => {
    return normalizeUserTypeCode(
        getFirstAttributeValue(
            tokenDetails,
            ['UserType', 'userType'],
            getFirstAttributeValue(tokenDetails?.attributes, ['UserType', 'userType'], '')
        )
    );
};

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const trimToEmpty = (value) => String(value ?? '').trim();

const stripLeadingSlashes = (value) => trimToEmpty(value).replace(/^\/+/, '');

const getPathLeaf = (value) => {
    const normalizedValue = trimToEmpty(value);
    if (!normalizedValue) {
        return '';
    }

    const segments = normalizedValue.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : normalizedValue;
};

const toGroupHideMatchers = (value) =>
    trimToEmpty(value)
        .split(',')
        .map((pattern) => trimToEmpty(pattern))
        .filter(Boolean)
        .filter((pattern, index, allPatterns) => allPatterns.indexOf(pattern) === index)
        .map((pattern) => new RegExp(`^${escapeRegExp(pattern).replace(/\\\*/g, '.*')}$`, 'i'));

const CreateRequestPage = ({ authDetails, onSignOut }) => {
    const tokenDetails = authDetails?.identity?.tokenParsed ?? {};
    const subject = authDetails?.identity?.tokenParsed?.sub ?? authDetails?.identity?.subject ?? '';
    const tokenSupervisorId = String(tokenDetails.supervisor ?? '').trim();
    const tokenUserType = extractUserTypeFromToken(tokenDetails);
    const [groups, setGroups] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
    const [requestViewMode, setRequestViewMode] = useState('system');
    const [supervisorName, setSupervisorName] = useState(() => tokenDetails.supervisor ?? '');
    const [resolvedUserType, setResolvedUserType] = useState(() => tokenUserType);
    const [supervisorDetails, setSupervisorDetails] = useState(null);
    const [supervisorPhotoUrl, setSupervisorPhotoUrl] = useState(null);
    const [isLoadingSupervisor, setIsLoadingSupervisor] = useState(false);
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [jobResponsibilities, setJobResponsibilities] = useState('');
    const [requestJustification, setRequestJustification] = useState('');
    const [requestedRoles] = useState([]);
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
    const [requestError, setRequestError] = useState('');
    const [expandedSystems, setExpandedSystems] = useState(new Set());
    const [groupsError, setGroupsError] = useState('');
    const [userGroupsError, setUserGroupsError] = useState('');
    const [isLoadingGroups, setIsLoadingGroups] = useState(true);
    const [isLoadingUserGroups, setIsLoadingUserGroups] = useState(true);
    const navigate = useNavigate();

    const ownedGroupIds = useMemo(() => {
        const ids = new Set(
            (Array.isArray(userGroups) ? userGroups : userGroups?.groups ?? [])
                .map((group) => group.id ?? group.name)
                .filter(Boolean)
        );
        return ids;
    }, [userGroups]);

    const requestableGroups = useMemo(() => {
        const hiddenGroupMatchers = toGroupHideMatchers(envConfig.hideGroups);
        if (hiddenGroupMatchers.length === 0) {
            return groups;
        }

        return groups.filter((group) => {
            const groupName = trimToEmpty(group?.name);
            const groupPath = trimToEmpty(group?.path);

            const candidateValues = [
                groupName,
                stripLeadingSlashes(groupName),
                groupPath,
                stripLeadingSlashes(groupPath),
                getPathLeaf(groupPath),
            ].filter(Boolean);

            if (candidateValues.length === 0) {
                return true;
            }

            return !hiddenGroupMatchers.some((matcher) =>
                candidateValues.some((candidate) => matcher.test(candidate))
            );
        });
    }, [groups]);

    const groupNameLookup = useMemo(() => {
        const lookup = new Map();
        requestableGroups.forEach((group) => {
            const groupId = String(group?.id ?? group?.name ?? '').trim();
            const groupName = String(group?.name ?? '').trim();
            if (groupId && groupName) {
                lookup.set(groupId, groupName);
            }
        });
        return lookup;
    }, [requestableGroups]);

    useEffect(() => {
        setResolvedUserType(tokenUserType);
    }, [tokenUserType]);

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;

        if (!apiUrl) {
            setGroupsError('API URL is not configured.');
            setUserGroupsError('API URL is not configured.');
            setIsLoadingGroups(false);
            setIsLoadingUserGroups(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchGroups = async () => {
            try {
                const response = await ApiClient.get("/groups");

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setGroups(Array.isArray(response.data) ? response.data : response.data?.groups ?? []);
                }
            } catch (error) {
                if (isMounted) {
                    setGroupsError('We could not load the available groups.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingGroups(false);
                }
            }
        };

        const fetchUserGroups = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}/groups`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setUserGroups(response.data.content);
                }
            } catch (error) {
                if (isMounted) {
                    setUserGroupsError('We could not load your current groups.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingUserGroups(false);
                }
            }
        };

        const fetchCurrentUserProfileFields = async () => {
            if (!subject || (tokenSupervisorId && tokenUserType)) {
                return;
            }

            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    const derivedSupervisorId = extractSupervisorIdFromUser(response.data ?? {});
                    if (derivedSupervisorId) {
                        setSupervisorName(derivedSupervisorId);
                    }
                    const derivedUserType = extractUserTypeFromUser(response.data ?? {});
                    if (derivedUserType) {
                        setResolvedUserType(derivedUserType);
                    }
                }
            } catch (error) {
                // No-op. Existing supervisorName/token value and submit validation remain in place.
            }
        };

        fetchGroups();
        fetchUserGroups();
        fetchCurrentUserProfileFields();

        return () => {
            isMounted = false;
        };
    }, [subject, tokenSupervisorId, tokenUserType]);

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;
        const supervisorId = String(supervisorName || '').trim();

        if (!supervisorId) {
            setSupervisorDetails(null);
            setSupervisorPhotoUrl(null);
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorDetails = async () => {
            setIsLoadingSupervisor(true);
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(supervisorId)}`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                if (isMounted) {
                    setSupervisorDetails(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorDetails(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisor(false);
                }
            }
        };

        const fetchSupervisorPhoto = async () => {
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(supervisorId)}/picture`, {
                    responseType: 'blob',
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const contentType = response.headers?.['content-type'] ?? '';
                if (contentType && !contentType.startsWith('image/')) {
                    throw new Error(`Unexpected content type: ${contentType}`);
                }

                objectUrl = URL.createObjectURL(response.data);

                if (isMounted) {
                    setSupervisorPhotoUrl(objectUrl);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorPhotoUrl(null);
                }
            }
        };

        fetchSupervisorDetails();
        fetchSupervisorPhoto();

        return () => {
            isMounted = false;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [supervisorName]);

    const toggleSelection = (groupId) => {
        setSelectedGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const toggleSystemExpansion = (systemName) => {
        setExpandedSystems((prev) => {
            const next = new Set(prev);
            if (next.has(systemName)) {
                next.delete(systemName);
            } else {
                next.add(systemName);
            }
            return next;
        });
    };

    const handleRequest = async (state) => {
        if (selectedGroupIds.size === 0) {
            return;
        }

        const bulk = selectedGroupIds.size > 1

        const supervisorUuid = supervisorDetails?.id ?? null;
        if (!supervisorUuid) {
            setRequestError('Supervisor details are missing. Please refresh and try again.');
            return;
        }

        const normalizedUserType = normalizeUserTypeCode(resolvedUserType);
        if (!normalizedUserType) {
            setRequestError('User type is missing. Update your profile and try again.');
            return;
        }

        let payload = {
            type: 'SYSTEM',
            securityManagerUuid: supervisorUuid,
            roles: requestedRoles,
            justification: requestJustification,
            jobResponsibilities,
        };
        const groupIds = Array.from(selectedGroupIds);
        const selectedGroupNames = groupIds
            .map((groupId) => groupNameLookup.get(String(groupId).trim()))
            .filter(Boolean);
        const requestPayloadForResults = {
            ...payload,
            state,
            groups: groupIds,
            groupNames: selectedGroupNames,
            securityManagerName: supervisorDisplayName || '',
            roleNames: requestedRoles,
        };
        payload = bulk ? {...payload, groups: groupIds } : { ...payload, groupUuid: groupIds[0] };

        setIsSubmittingRequest(true);
        setRequestError('');

        try {
            const response = await ApiClient.post(bulk ? '/access-requests/bulk' : '/access-requests', payload);
            navigate('/requestResults', {
                state: {
                    response: response.data,
                    requestPayload: requestPayloadForResults,
                },
            });
        } catch (error) {
            const errorResponseData = error?.response?.data ?? null;
            const errorStatus = error?.response?.status ?? null;
            const errorStatusText = error?.response?.statusText ?? null;
            const errorDetails = {
                status: errorStatus,
                statusText: errorStatusText,
                data: errorResponseData,
            };

            setRequestError('We could not submit your access request. Please review the error details.');
            navigate('/requestResults', {
                state: {
                    response: null,
                    requestPayload: requestPayloadForResults,
                    error: errorDetails,
                },
            });
        } finally {
            setIsSubmittingRequest(false);
        }
    };

    const isLoading = isLoadingGroups || isLoadingUserGroups;
    const hasError = groupsError || userGroupsError;

    const filteredGroups = useMemo(() => {
        if (!groupSearchTerm.trim()) {
            return requestableGroups;
        }
        const search = groupSearchTerm.trim().toLowerCase();
        return requestableGroups.filter((group) => (group?.name ?? '').toLowerCase().includes(search));
    }, [requestableGroups, groupSearchTerm]);

    const groupedSystems = useMemo(() => {
        const systemMap = new Map();

        filteredGroups.forEach((group) => {
            const name = group?.name ?? '';
            const systemName = String(name).split('-')[0]?.trim() || 'Unknown';
            if (!systemMap.has(systemName)) {
                systemMap.set(systemName, []);
            }
            systemMap.get(systemName).push(group);
        });

        return Array.from(systemMap.entries()).map(([system, systemGroups]) => ({
            system,
            groups: systemGroups,
        }));
    }, [filteredGroups]);

    const systemImageMap = useMemo(
        () => ({
            AWS: '/aws-system.png',
            APPGATE: '/appgate-logo.webp',
            CYBERARK: '/cyberark-system.png',
            JCWA: '/USCC_Logo.png',
            KEYCLOAK: '/keycloak-system.webp',
            UNLOCK: '/unlock-system.png',
            UPOC: '/unifiedplatform_logo.png',
        }),
        []
    );

    const renderGroupOption = (group) => {
        const groupId = group.id ?? group.name;
        const isOwned = ownedGroupIds.has(groupId);

        return (
            <div key={groupId} className="usa-checkbox">
                <input
                    className="usa-checkbox__input usa-checkbox__input--tile"
                    id={`request-group-${groupId}`}
                    type="checkbox"
                    name="request-groups"
                    checked={selectedGroupIds.has(groupId)}
                    onChange={() => toggleSelection(groupId)}
                    disabled={isOwned}
                />
                <label className="usa-checkbox__label" htmlFor={`request-group-${groupId}`}>
                    {group.name}
                    <span className="usa-checkbox__label-description">
                        <DescriptionWithTags text={group.description} />
                    </span>
                    {isOwned ? (
                        <span className="usa-checkbox__label-description">
                            You already have access to this group.
                        </span>
                    ) : null}
                </label>
            </div>
        );
    };

    const supervisorDisplayName =
        firstResolvedDisplayName([
            supervisorDetails?.name,
            `${supervisorDetails?.firstName || ''} ${supervisorDetails?.lastName || ''}`.trim(),
            supervisorDetails?.preferred_username,
            supervisorDetails?.username,
        ], '');

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Request new access</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoading ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 1: Select your supervisor</h3>
                                                    <div className="supervisor-summary">
                                                        <div className="supervisor-summary__avatar" />
                                                        <div className="supervisor-summary__details">
                                                            <p className="supervisor-summary__name">
                                                                <ValueWithSkeleton loading type="sentence" width="14ch" />
                                                            </p>
                                                            <p className="supervisor-summary__meta">
                                                                <ValueWithSkeleton loading type="sentence" width="20ch" />
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 2: Select groups to request</h3>
                                                    <div className="usa-fieldset">
                                                        {Array.from({ length: 4 }).map((_, index) => (
                                                            <div key={`request-group-loading-${index}`} className="usa-checkbox">
                                                                <input
                                                                    className="usa-checkbox__input usa-checkbox__input--tile"
                                                                    type="checkbox"
                                                                    disabled
                                                                />
                                                                <label className="usa-checkbox__label">
                                                                    <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                                    <span className="usa-checkbox__label-description">
                                                                        <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : hasError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{groupsError || userGroupsError}</p>
                                                </div>
                                            </div>
                                        ) : requestableGroups.length === 0 ? (
                                            <p>No groups are available for requests right now.</p>
                                        ) : (
                                            <div className="usa-form">
                                                {requestError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">{requestError}</p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 1: Select your supervisor</h3>
                                                    <div className="supervisor-summary">
                                                        <div
                                                            className={
                                                                supervisorPhotoUrl
                                                                    ? 'supervisor-summary__avatar supervisor-summary__avatar--photo'
                                                                    : 'supervisor-summary__avatar'
                                                            }
                                                            aria-hidden={!supervisorPhotoUrl}
                                                        >
                                                            {supervisorPhotoUrl && !isLoadingSupervisor ? (
                                                                <img
                                                                    className="supervisor-summary__avatar-image"
                                                                    src={supervisorPhotoUrl}
                                                                    alt={supervisorDisplayName || 'Supervisor'}
                                                                />
                                                            ) : null}
                                                        </div>
                                                        <div className="supervisor-summary__details">
                                                            <p className="supervisor-summary__name">
                                                                {isLoadingSupervisor
                                                                    ? <ValueWithSkeleton loading type="sentence" width="14ch" />
                                                                    : supervisorDisplayName || 'Supervisor not found'}
                                                            </p>
                                                            {isLoadingSupervisor ? (
                                                                <p className="supervisor-summary__meta" aria-hidden="true">
                                                                    <ValueWithSkeleton loading type="sentence" width="20ch" />
                                                                </p>
                                                            ) : supervisorDetails?.email ? (
                                                                <p className="supervisor-summary__meta">
                                                                    <CopyableEmail
                                                                        email={supervisorDetails.email}
                                                                        currentUserEmail={tokenDetails.email}
                                                                        fallback={null}
                                                                    />
                                                                </p>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 2: Select groups to request</h3>
                                                    <div className="request-step__actions">
                                                        <div className="request-step__search">
                                                            <input
                                                                className="usa-input"
                                                                id="group-search"
                                                                name="group-search"
                                                                type="search"
                                                                value={groupSearchTerm}
                                                                onChange={(event) => setGroupSearchTerm(event.target.value)}
                                                                placeholder="Search"
                                                            />
                                                        </div>
                                                        <div className="request-view-toggle" role="group">
                                                            <button
                                                                type="button"
                                                                className={`request-view-toggle__icon-button${requestViewMode === 'system' ? ' request-view-toggle__icon-button--active' : ''
                                                                    }`}
                                                                onClick={() => setRequestViewMode('system')}
                                                                aria-pressed={requestViewMode === 'system'}
                                                                aria-label="System view"
                                                            >
                                                                <img
                                                                    src="/icons/view-compact-alt.svg"
                                                                    alt=""
                                                                    aria-hidden="true"
                                                                    className="request-view-toggle__icon"
                                                                />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`request-view-toggle__icon-button${requestViewMode === 'group' ? ' request-view-toggle__icon-button--active' : ''
                                                                    }`}
                                                                onClick={() => setRequestViewMode('group')}
                                                                aria-pressed={requestViewMode === 'group'}
                                                                aria-label="Group view"
                                                            >
                                                                <img
                                                                    src="/icons/view-list.svg"
                                                                    alt=""
                                                                    aria-hidden="true"
                                                                    className="request-view-toggle__icon"
                                                                />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {requestViewMode === 'system' ? (
                                                        <div className="request-system-grid">
                                                            {groupedSystems.map((system) => {
                                                                const isExpanded = expandedSystems.has(system.system);
                                                                const systemImageSrc = systemImageMap[system.system] ?? null;
                                                                return (
                                                                    <div key={system.system} className="usa-card request-system-card">
                                                                        <div className="usa-card__container">
                                                                            <button
                                                                                type="button"
                                                                                className="request-system-card__toggle"
                                                                                onClick={() => toggleSystemExpansion(system.system)}
                                                                                aria-expanded={isExpanded}
                                                                            >
                                                                                <span
                                                                                    className={`usa-card__header request-system-card__header${systemImageSrc ? ' request-system-card__header--image' : ''
                                                                                        }`}
                                                                                >
                                                                                    {systemImageSrc ? (
                                                                                        <>
                                                                                            <img
                                                                                                className="request-system-card__image request-system-card__image--photo"
                                                                                                src={systemImageSrc}
                                                                                                alt={system.system}
                                                                                            />
                                                                                            {['JCWA', 'UPOC'].includes(system.system) ? (
                                                                                                <h3 className="usa-card__heading request-system-card__label">
                                                                                                    {system.system}
                                                                                                </h3>
                                                                                            ) : null}
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <span className="request-system-card__image" aria-hidden="true" />
                                                                                            <h3 className="usa-card__heading">{system.system}</h3>
                                                                                        </>
                                                                                    )}
                                                                                </span>
                                                                                <span className="request-system-card__chevron" aria-hidden="true">
                                                                                    {isExpanded ? '▾' : '▸'}
                                                                                </span>
                                                                            </button>
                                                                            {isExpanded ? (
                                                                                <div className="usa-card__body">
                                                                                    <fieldset className="usa-fieldset">
                                                                                        {system.groups.map((group) => renderGroupOption(group))}
                                                                                    </fieldset>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <fieldset className="usa-fieldset">
                                                            {filteredGroups.map((group) => renderGroupOption(group))}
                                                        </fieldset>
                                                    )}
                                                </div>
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 3: Job responsibilities</h3>
                                                    <label className="usa-label" htmlFor="request-responsibilities">
                                                        Describe your job responsibilities
                                                    </label>
                                                    <textarea
                                                        className="usa-textarea"
                                                        id="request-responsibilities"
                                                        name="request-responsibilities"
                                                        rows="4"
                                                        value={jobResponsibilities}
                                                        onChange={(event) => setJobResponsibilities(event.target.value)}
                                                        placeholder="Summarize the responsibilities that require this access"
                                                    />
                                                </div>
                                                <div className="request-step">
                                                    <h3 className="request-step__heading">Step 4: Request justification</h3>
                                                    <label className="usa-label" htmlFor="request-justification">
                                                        Provide a justification for this request
                                                    </label>
                                                    <textarea
                                                        className="usa-textarea"
                                                        id="request-justification"
                                                        name="request-justification"
                                                        rows="4"
                                                        value={requestJustification}
                                                        onChange={(event) => setRequestJustification(event.target.value)}
                                                        placeholder="Explain why this access is needed"
                                                    />
                                                </div>
                                                <input
                                                    type="hidden"
                                                    id="request-roles"
                                                    name="request-roles"
                                                    value={requestedRoles.join(',')}
                                                    readOnly
                                                />
                                                <div className="usa-button-group">
                                                    <button
                                                        type="button"
                                                        className="usa-button"
                                                        onClick={() => handleRequest('SUBMITTED')}
                                                        disabled={selectedGroupIds.size === 0 || isSubmittingRequest}
                                                    >
                                                        {isSubmittingRequest ? 'Submitting...' : 'Request'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default CreateRequestPage;
